/**
 * FormListWidget — Forms/assessments for subject (DRR-321)
 *
 * Shows tasks with FORM task type for the current subject.
 * Uses generated useGetTasksBySubjectQuery hook.
 */

import React, { useMemo } from 'react';
import {
  Stack,
  Text,
  Badge,
  Group,
  Card,
  Skeleton,
  Alert,
  Box,
  ScrollArea,
  Progress,
  ThemeIcon,
} from '@mantine/core';
import {
  IconForms,
  IconAlertCircle,
  IconClock,
  IconChevronRight,
  IconCircleCheck,
  IconCircleDashed,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useGetTasksBySubjectQuery, GqlTaskStatus } from '@/generated/graphql';
import type { WidgetProps } from '@ferp/core';

export function FormListWidget({ subjectActorId, onNavigate }: WidgetProps) {
  const navigate = useNavigate();

  const { data, loading, error } = useGetTasksBySubjectQuery({
    variables: { subjectActorId: subjectActorId ?? '' },
    skip: !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  // Filter for form-related tasks (by name pattern)
  const forms = useMemo(() => {
    const tasks = data?.taskApiGetTasksBySubject ?? [];
    return tasks.filter(t =>
      t.name.toLowerCase().includes('form') ||
      t.name.toLowerCase().includes('assessment') ||
      t.name.toLowerCase().includes('questionnaire')
    );
  }, [data]);

  const { pendingForms, completedForms } = useMemo(() => {
    return {
      pendingForms: forms.filter(f =>
        f.status === GqlTaskStatus.PENDING ||
        f.status === GqlTaskStatus.ASSIGNED ||
        f.status === GqlTaskStatus.IN_PROGRESS
      ),
      completedForms: forms.filter(f => f.status === GqlTaskStatus.COMPLETED),
    };
  }, [forms]);

  const handleFormClick = (taskId: string) => {
    const path = `/ferp-core/tasks/${taskId}`;
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  if (loading && forms.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={50} />
        <Skeleton height={50} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Error loading forms.
      </Alert>
    );
  }

  if (forms.length === 0) {
    return (
      <Alert icon={<IconForms size={16} />} color="blue" variant="light">
        No forms for this subject.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <IconForms size={16} color="var(--mantine-color-blue-6)" />
        <Text fw={600} size="sm">Forms</Text>
        {pendingForms.length > 0 && (
          <Badge size="sm" color="yellow">{pendingForms.length} pending</Badge>
        )}
      </Group>

      <ScrollArea.Autosize mah={200}>
        <Stack gap="xs">
          {pendingForms.map((form) => (
            <Card
              key={form.id}
              withBorder
              padding="xs"
              radius="sm"
              style={{ cursor: 'pointer' }}
              onClick={() => handleFormClick(form.id)}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                  <ThemeIcon size="sm" variant="light" color="blue">
                    <IconCircleDashed size={14} />
                  </ThemeIcon>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} lineClamp={1}>{form.name}</Text>
                    {form.dueAt && (
                      <Group gap={4}>
                        <IconClock size={10} color="gray" />
                        <Text size="xs" c="dimmed">
                          Due: {new Date(form.dueAt).toLocaleDateString()}
                        </Text>
                      </Group>
                    )}
                  </Box>
                </Group>
                {form.progressPercent != null && form.progressPercent > 0 && (
                  <Progress value={form.progressPercent} size="sm" w={60} />
                )}
                <IconChevronRight size={14} color="gray" />
              </Group>
            </Card>
          ))}

          {completedForms.length > 0 && (
            <Text size="xs" c="dimmed" ta="center">
              {completedForms.length} completed form{completedForms.length !== 1 ? 's' : ''}
            </Text>
          )}
        </Stack>
      </ScrollArea.Autosize>
    </Stack>
  );
}
